// Permission vocabulary — single source of truth for the intranet's
// team-based access control. 30 keys, grouped by domain. The server
// mirrors this file at `server/src/core/constants/permissions.js`
// and must stay in sync (see NEEDED_FOR_SERVER.md §I11).
//
// Effective permissions for a user in an org =
//   owner-bypass  ∪  OrgMember.role perms  ∪  team perms  ∪  ancestor team perms
//
// Checked in:
//   • Governance RLS (`jwt_has_perm(key)` — migration 0060)
//   • Server endpoints that mutate teams / perms
//   • Intranet UI gates (admin matrix, tab visibility)
//
// No wildcards by design — enumerate effective perms exactly.

import { shellResolveClaims } from './claimResolver.js'

// ── Permission keys ────────────────────────────────────────────────

const BASE_PERMISSIONS = {
  // Admin (settings-page surfaces; not "superuser")
  'admin.pages.read': {
    group: 'Admin',
    label: 'admin.perm.adminPagesRead.label',
    description: 'admin.perm.adminPagesRead.description'
  },
  'admin.users': {
    group: 'Admin',
    label: 'admin.perm.adminUsers.label',
    description: 'admin.perm.adminUsers.description'
  },
  'admin.teams': {
    group: 'Admin',
    label: 'admin.perm.adminTeams.label',
    description: 'admin.perm.adminTeams.description'
  },
  'admin.team_permissions': {
    group: 'Admin',
    label: 'admin.perm.adminTeamPermissions.label',
    description: 'admin.perm.adminTeamPermissions.description'
  },
  'admin.billing': {
    group: 'Admin',
    label: 'admin.perm.adminBilling.label',
    description: 'admin.perm.adminBilling.description'
  },
  'admin.integrations': {
    group: 'Admin',
    label: 'admin.perm.adminIntegrations.label',
    description: 'admin.perm.adminIntegrations.description'
  },
  'admin.commercials': {
    group: 'Admin',
    label: 'admin.perm.adminCommercials.label',
    description: 'admin.perm.adminCommercials.description'
  },
  'admin.cap_table': {
    group: 'Admin',
    label: 'admin.perm.adminCapTable.label',
    description: 'admin.perm.adminCapTable.description'
  },
  'admin.hr': {
    group: 'Admin',
    label: 'admin.perm.adminHr.label',
    description: 'admin.perm.adminHr.description'
  },
  'admin.logs': {
    group: 'Admin',
    label: 'admin.perm.adminLogs.label',
    description: 'admin.perm.adminLogs.description'
  },
  'admin.system': {
    group: 'Admin',
    label: 'admin.perm.adminSystem.label',
    description: 'admin.perm.adminSystem.description'
  },
  'admin.company': {
    group: 'Admin',
    label: 'admin.perm.adminCompany.label',
    description: 'admin.perm.adminCompany.description'
  },

  // Docs
  'docs.edit_others': {
    group: 'Docs',
    label: 'admin.perm.docsEditOthers.label',
    description: 'admin.perm.docsEditOthers.description'
  },
  'docs.delete_others': {
    group: 'Docs',
    label: 'admin.perm.docsDeleteOthers.label',
    description: 'admin.perm.docsDeleteOthers.description'
  },
  'docs.read_private': {
    group: 'Docs',
    label: 'admin.perm.docsReadPrivate.label',
    description: 'admin.perm.docsReadPrivate.description'
  },

  // Knowledge base
  'kb.edit_others': {
    group: 'KB',
    label: 'admin.perm.kbEditOthers.label',
    description: 'admin.perm.kbEditOthers.description'
  },
  'kb.delete_others': {
    group: 'KB',
    label: 'admin.perm.kbDeleteOthers.label',
    description: 'admin.perm.kbDeleteOthers.description'
  },

  // Tasks (né Tickets — user-facing rename 2026-08-06; permission KEYS stay
  // the internal tickets.* vocabulary the server checks against)
  'tickets.assign': {
    group: 'Tasks',
    label: 'admin.perm.ticketsAssign.label',
    description: 'admin.perm.ticketsAssign.description'
  },
  'tickets.close_others': {
    group: 'Tasks',
    label: 'admin.perm.ticketsCloseOthers.label',
    description: 'admin.perm.ticketsCloseOthers.description'
  },
  'tickets.delete_others': {
    group: 'Tasks',
    label: 'admin.perm.ticketsDeleteOthers.label',
    description: 'admin.perm.ticketsDeleteOthers.description'
  },
  'tickets.manage_columns': {
    group: 'Tasks',
    label: 'admin.perm.ticketsManageColumns.label',
    description: 'admin.perm.ticketsManageColumns.description'
  },

  // Stories / announcements
  'stories.post': {
    group: 'Stories',
    label: 'admin.perm.storiesPost.label',
    description: 'admin.perm.storiesPost.description'
  },
  'stories.edit_others': {
    group: 'Stories',
    label: 'admin.perm.storiesEditOthers.label',
    description: 'admin.perm.storiesEditOthers.description'
  },
  'stories.delete_others': {
    group: 'Stories',
    label: 'admin.perm.storiesDeleteOthers.label',
    description: 'admin.perm.storiesDeleteOthers.description'
  },
  'stories.pin': {
    group: 'Stories',
    label: 'admin.perm.storiesPin.label',
    description: 'admin.perm.storiesPin.description'
  },

  // Calendar
  'calendar.create_org_events': {
    group: 'Calendar',
    label: 'admin.perm.calendarCreateOrgEvents.label',
    description: 'admin.perm.calendarCreateOrgEvents.description'
  },
  'calendar.edit_others': {
    group: 'Calendar',
    label: 'admin.perm.calendarEditOthers.label',
    description: 'admin.perm.calendarEditOthers.description'
  },

  // Chat
  'chat.create_channels': {
    group: 'Chat',
    label: 'admin.perm.chatCreateChannels.label',
    description: 'admin.perm.chatCreateChannels.description'
  },
  'chat.archive_channels': {
    group: 'Chat',
    label: 'admin.perm.chatArchiveChannels.label',
    description: 'admin.perm.chatArchiveChannels.description'
  },
  'chat.delete_others': {
    group: 'Chat',
    label: 'admin.perm.chatDeleteOthers.label',
    description: 'admin.perm.chatDeleteOthers.description'
  },

  // Grants
  'grants.view_others': {
    group: 'Grants',
    label: 'admin.perm.grantsViewOthers.label',
    description: 'admin.perm.grantsViewOthers.description'
  }
}

// ── App-access CRUD permissions (team app-access matrix) ───────────
// Mirror of server/src/core/constants/permissions.js APP_ACCESS_VERBS /
// APP_ACCESS_APPS / APP_PERMISSIONS (server@554832fb) — same keys, same
// labels, same order, group 'Apps'. A structured second namespace,
// orthogonal to the flat admin/docs/kb/… keys above: `app.<appKey>.<verb>`
// gates per-app CRUD on the gateable workspace apps. The server already
// folds these into its PERMISSION_KEY_SET (team-permission writes accept
// them today) — this client-side mirror is what lets /admin/teams render
// + write them through the existing matrix machinery below
// (PERMISSION_KEYS / PERMISSION_GROUPS / PERMISSIONS_BY_GROUP all derive
// from the merged PERMISSIONS object, so folding APP_PERMISSIONS in here
// is the only wiring the rest of this file needs).
export const APP_ACCESS_VERBS = ['read', 'create', 'update', 'delete']

// `label` carries a BUNDLE KEY, resolved at every render site through
// polyglot (`el.call('permLabelText', …)` below, and the app-access matrix
// header in pages/admin/teams.js).
export const APP_ACCESS_APPS = [
  { key: 'calendar', label: 'admin.app.calendar' },
  { key: 'chat', label: 'admin.app.chat' },
  { key: 'meet', label: 'admin.app.meet' },
  { key: 'documents', label: 'admin.app.documents' },
  { key: 'files', label: 'admin.app.files' },
  { key: 'tickets', label: 'admin.app.tickets' },
  { key: 'reminders', label: 'admin.app.reminders' },
  { key: 'resolutions', label: 'admin.app.resolutions' },
  { key: 'analytics', label: 'admin.app.analytics' },
  { key: 'data', label: 'admin.app.data' },
  { key: 'announcements', label: 'admin.app.announcements' },
  { key: 'company', label: 'admin.app.company' }
]

// The four CRUD verbs, as bundle keys. Written out rather than derived from
// APP_ACCESS_VERBS so every key is greppable (docs/I18N-CONVENTION.md §1.3).
const APP_VERB_KEYS = {
  read: 'admin.perm.verb.read',
  create: 'admin.perm.verb.create',
  update: 'admin.perm.verb.update',
  delete: 'admin.perm.verb.delete'
}

// Generated: every `app.<key>.<verb>` combination — 12 apps × 4 verbs =
// 48 keys — grouped under 'Apps' so the matrix UI can render per-app
// rows via PERMISSIONS_BY_GROUP.Apps.
// `appKey` / `verbKey` are the two bundle keys the label and description are
// COMPOSED from — 48 mechanical sentences would otherwise need 96 hand-written
// keys. `permLabelText` / `permDescriptionText` below do the composition.
export const APP_PERMISSIONS = APP_ACCESS_APPS.reduce((acc, app) => {
  for (const verb of APP_ACCESS_VERBS) {
    acc[`app.${app.key}.${verb}`] = {
      group: 'Apps',
      appKey: app.label,
      verbKey: APP_VERB_KEYS[verb]
    }
  }
  return acc
}, {})

// Canonical catalog — BASE_PERMISSIONS (30 admin/docs/kb/… keys) folded
// together with the generated APP_PERMISSIONS (48 app.* keys) so every
// downstream consumer derived from PERMISSIONS (PERMISSION_KEYS,
// PERMISSION_GROUPS, PERMISSIONS_BY_GROUP, permLabel/permDescription/
// permGroup) sees both namespaces with zero extra wiring.
export const PERMISSIONS = { ...BASE_PERMISSIONS, ...APP_PERMISSIONS }

export const PERMISSION_KEYS = Object.keys(PERMISSIONS)
export const PERMISSION_GROUPS = [...new Set(PERMISSION_KEYS.map((k) => PERMISSIONS[k].group))]

// ── OrgMember.role → implicit perm sets ────────────────────────────
// Union with team perms. `owner` bypasses at the claim-synthesis
// layer — exposed here only for reference / explicit checks.

export const ORG_ROLE_PERMISSIONS = {
  owner: PERMISSION_KEYS, // bypass; every key
  admin: PERMISSION_KEYS.filter((k) => k.startsWith('admin.')), // all admin.*
  member: []
}

// ── System team seed defaults ──────────────────────────────────────
// Used by `seed-system-teams.mjs` on the server. Admins can edit
// each team's perms after seed. Slugs are the stable identifier;
// display names can be changed per-org.

export const SYSTEM_TEAMS = [
  // Active category — members of these teams see the full intranet.
  { slug: 'members', name: 'Members', category: 'active', permissions: [] },
  {
    slug: 'admin',
    name: 'Admin',
    category: 'active',
    permissions: PERMISSION_KEYS.filter((k) => k.startsWith('admin.'))
  },
  {
    slug: 'manager',
    name: 'Manager',
    category: 'active',
    permissions: [
      'tickets.assign',
      'tickets.close_others',
      'calendar.create_org_events',
      'stories.post',
      'docs.edit_others'
    ]
  },
  { slug: 'hr', name: 'HR', category: 'active', permissions: ['admin.hr', 'admin.users'] },
  {
    slug: 'finance',
    name: 'Finance',
    category: 'active',
    permissions: ['admin.billing', 'admin.cap_table', 'grants.view_others']
  },
  {
    slug: 'content',
    name: 'Content',
    category: 'active',
    permissions: ['docs.edit_others', 'kb.edit_others', 'stories.post', 'stories.pin']
  },

  // External category — trimmed intranet surface.
  { slug: 'advisors', name: 'Advisors', category: 'external', permissions: [] },
  { slug: 'investors', name: 'Investors', category: 'external', permissions: [] },
  { slug: 'alumni', name: 'Alumni', category: 'external', permissions: [] }
]

export const SYSTEM_TEAM_SLUGS = new Set(SYSTEM_TEAMS.map((t) => t.slug))

// Team category enum. §unified-perms (2026-05-23): expanded 3 → 5.
// Precedence order [manager > active > passive > external > archived]
// resolves a multi-team user's "dominant category" for UI grouping.
// Mirrors server/src/core/constants/permissions.js TEAM_CATEGORIES —
// keep in sync.
export const CATEGORIES = ['manager', 'active', 'passive', 'external', 'archived']

// CATEGORY_PERMISSION_TEMPLATES — default elevation set a team receives
// when its category is selected. Teams customize on top; the team's
// `appliedTemplate` field (null = customized) drives the UI badge.
//
// Manager  — every key (admin overlay)
// Active   — common moderation elevations
// Passive  — zero elevations (read-only via category gating)
// External — zero elevations
// Archived — zero elevations + hidden from rolls
//
// Mirrors server constants. Keep in sync.
export const CATEGORY_PERMISSION_TEMPLATES = {
  manager: PERMISSION_KEYS,
  active: [
    'tickets.assign',
    'tickets.close_others',
    'tickets.manage_columns',
    'calendar.create_org_events',
    'stories.post',
    'chat.create_channels',
    'docs.edit_others',
    'kb.edit_others'
  ],
  passive: [],
  external: [],
  archived: []
}

// Pure helper: returns true when team.permissions[] matches the
// CATEGORY_PERMISSION_TEMPLATES entry for team.category. Drives the
// "Template applied" vs "Customized" badge in /admin/teams.
export const teamMatchesTemplate = (team) => {
  if (!team || !team.category) return false
  const tpl = CATEGORY_PERMISSION_TEMPLATES[team.category]
  if (!tpl) return false
  const perms = Array.isArray(team.permissions) ? team.permissions : []
  if (perms.length !== tpl.length) return false
  const tplSet = new Set(tpl)
  return perms.every((p) => tplSet.has(p))
}

// Resolve a user's dominant category from their team memberships.
// Returns the first category in CATEGORIES that matches any team.
// Used by /admin to group people by their primary category.
export const dominantCategory = (teams) => {
  if (!Array.isArray(teams) || !teams.length) return null
  const set = new Set(teams.map((t) => t.category).filter(Boolean))
  for (const cat of CATEGORIES) if (set.has(cat)) return cat
  return null
}

// Orgs where `public.people` is the legacy roster — `user_profiles`
// rows predate multi-tenancy and were never workspace-scoped, so these
// orgs trust the view directly instead of intersecting with Mongo
// OrgMember. Drop an id here once its user_profiles have been fully
// provisioned as Mongo Users + OrgMembers.
export const LEGACY_ORG_IDS = new Set([
  '69b77a61e0e0de1aa47d01cb' // Symbols System — original pre-multitenancy home
])

// Precomputed at module load so render factories don't re-filter the
// 30-key list per group per tick.
export const PERMISSIONS_BY_GROUP = Object.fromEntries(
  PERMISSION_GROUPS.map((g) => [g, PERMISSION_KEYS.filter((k) => PERMISSIONS[k].group === g)])
)

// ── Runtime helpers — consume JWT claims ───────────────────────────
// Claims shape is set by symbols-auth-bridge: `is_owner`, `org_role`,
// `teams[{id,slug,category}]`, `permissions[]`.

export const hasPerm = (claims, key) =>
  !!claims && (claims.is_owner === true || (claims.permissions || []).includes(key))

// `claims*` prefix, deliberately. These take a JWT CLAIMS object and answer a
// claims-scoped question. `functions/roleHelpers.js` exports `isActiveMember` /
// `isAdmin` too, but those take component STATE and answer a different question
// (roleHelpers' isActiveMember = "signed in AND present in root.people"; this
// one = "belongs to an active-category team"), and roleHelpers IS star-exported
// from functions/index.js — so it owns those bare names for `el.call`. This file
// is currently only direct-imported, which is the only reason the clash never
// fired; star-exporting it later would have silently swapped one meaning for the
// other at every call site. Distinct names remove that trap (Rule 63).
export const claimsIsActiveMember = (claims) =>
  !!claims && (claims.teams || []).some((t) => t.category === 'active')

export const isInTeam = (claims, slug) =>
  !!claims && (claims.teams || []).some((t) => t.slug === slug)

export const claimsIsOwner = (claims) => !!claims && claims.is_owner === true

export const claimsIsAdmin = (claims) =>
  !!claims && (claims.org_role === 'owner' || claims.org_role === 'admin')

// Is the user allowed to see any gov UI surface beyond the external
// trim? Owners/admins bypass the team check; otherwise must belong to
// an active-category team.
export const canAccessGovUI = (claims) =>
  claimsIsOwner(claims) || claimsIsAdmin(claims) || claimsIsActiveMember(claims)

// Effective category from a list of team entries: active beats
// §unified-perms PR #4 (2026-05-23): expanded to handle the 5-category
// enum. Returns the FIRST category from CATEGORIES (precedence order
// manager > active > passive > external > archived) that any of the
// person's teams belongs to, else 'none'. Same as `dominantCategory`
// but with a 'none' fallback for people with no team memberships.
export const effectiveCategory = (teams) => {
  if (!teams || !teams.length) return 'none'
  const set = new Set(teams.map((t) => t.category).filter(Boolean))
  for (const cat of CATEGORIES) if (set.has(cat)) return cat
  return 'none'
}

// Redirect-gate for pages gated on active-member access (Chat,
// Tickets, Documents). Returns true if a redirect was issued.
export const redirectIfExternalOnly = function (s) {
  const el = this
  // Capstone GATE A: route the claim read through the resolver so this gate
  // stays consistent with roleHelpers (Mongo-sourced via `/auth/me`).
  const claims = shellResolveClaims(s?.root)
  // Claims still loading — don't bounce.
  if (!claims || !Array.isArray(claims.teams)) return false
  if (canAccessGovUI(claims)) return false
  const root = el.call('findRouteRoot', el)
  if (root) el.router('/', root, {}, { pushState: true })
  return true
}

// The RAW catalog reads. `permLabel` returns a BUNDLE KEY (or the permission
// key itself when the catalog does not know it), never display copy — use
// `permLabelText` / `permDescriptionText` at any render site.
export const permLabel = (key) => PERMISSIONS[key]?.label || key
export const permDescription = (key) => PERMISSIONS[key]?.description || ''
export const permGroup = (key) => PERMISSIONS[key]?.group || 'Other'

// Group names double as OBJECT KEYS in PERMISSIONS_BY_GROUP and as branch
// values (`s.group === 'Apps'`), so they stay English identifiers in the data
// and translate through this lookup at the render site — never by building
// `'admin.permGroup.' + s.group`, which no grep and no parity test can see.
export const PERMISSION_GROUP_KEYS = {
  Admin: 'admin.permGroup.admin',
  Docs: 'admin.permGroup.docs',
  KB: 'admin.permGroup.kb',
  Tasks: 'admin.permGroup.tasks',
  Stories: 'admin.permGroup.stories',
  Calendar: 'admin.permGroup.calendar',
  Chat: 'admin.permGroup.chat',
  Grants: 'admin.permGroup.grants',
  Apps: 'admin.permGroup.apps',
  Other: 'admin.permGroup.other'
}

// Element-bound display helpers. `this` IS the element at any arity (a
// functions/ export is invoked as fn.call(element, …)), so polyglot reads
// root.lang through the state proxy and the caller's effect re-runs on a
// language switch.
export const permLabelText = function (key) {
  const p = PERMISSIONS[key]
  if (!p) return key
  if (p.appKey) {
    return this.call('polyglot', 'admin.perm.appLabel')
      .replace('{app}', this.call('polyglot', p.appKey))
      .replace('{verb}', this.call('polyglot', p.verbKey))
  }
  return this.call('polyglot', p.label)
}

export const permDescriptionText = function (key) {
  const p = PERMISSIONS[key]
  if (!p) return ''
  if (p.appKey) {
    return this.call('polyglot', 'admin.perm.appDescription')
      .replace('{app}', this.call('polyglot', p.appKey))
      .replace('{verb}', this.call('polyglot', p.verbKey))
  }
  return this.call('polyglot', p.description)
}

export const permGroupText = function (key) {
  return this.call('polyglot', PERMISSION_GROUP_KEYS[key] || PERMISSION_GROUP_KEYS.Other)
}
